import {
  type ImpersonateUserMutation,
  type ImpersonateUserMutationVariables,
  type MutationFunctionOptions,
  type MutationHookOptions,
  useImpersonateUserMutation,
} from "@qw/graphql";
import { useAtom } from "jotai";

import { authDataAtom, authTokenValidatedAtom, impersonatorAuthDataAtom } from "../atoms";
import { useAuthContext } from "./use-auth-context";

export function useImpersonateUser(
  options: MutationHookOptions<ImpersonateUserMutation, ImpersonateUserMutationVariables> = {}
) {
  const [authData, setAuthData] = useAtom(authDataAtom);
  const [, setImpersonatorAuthData] = useAtom(impersonatorAuthDataAtom);
  const [, setTokenValidated] = useAtom(authTokenValidatedAtom);
  const { authClientId } = useAuthContext();

  const [mutate, result] = useImpersonateUserMutation(options);

  const impersonateUser = (
    mutationOptions: MutationFunctionOptions<
      ImpersonateUserMutation,
      Omit<ImpersonateUserMutationVariables, "authClientId">
    >
  ) => {
    if (!mutationOptions.variables?.userIdToImpersonate && !mutationOptions.variables?.emailToImpersonate) {
      return Promise.reject(new Error("userIdToImpersonate or emailToImpersonate is required"));
    }

    setImpersonatorAuthData(authData);

    return mutate({
      ...mutationOptions,
      context: {
        headers: {
          Authorization: `Bearer ${authData?.token.accessToken}`,
        },
      },
      variables: { ...mutationOptions.variables, authClientId },
      onCompleted: (data) => {
        void mutationOptions.onCompleted?.(data);

        setAuthData({
          token: { accessToken: data.impersonateUser.accessToken },
          shouldRemember: true,
        });
        setTokenValidated(true);
      },
      refetchQueries: ["MyAccount"],
    });
  };

  return [impersonateUser, result] as const;
}
